// Single source for the reminder statuses the console knows about. Mirrors the
// backend ReminderStatus enum, which the API validates filter values against:
// a status missing here is a filter the user cannot reach, and one invented
// here comes back as a 400.

export const REMINDER_STATUSES = ['pending', 'sent', 'delivered', 'failed', 'cancelled'] as const;

export type ReminderStatusKey = (typeof REMINDER_STATUSES)[number];

/**
 * Labels for filter dropdowns. StatusBadge owns how a status looks on a row.
 *
 * 'failed' means a send was attempted and went wrong. 'cancelled' means none
 * was attempted, because sending would have been wrong: the visit already
 * happened, there is no usable number, or the send date is long past.
 */
export const REMINDER_STATUS_LABELS: Record<ReminderStatusKey, string> = {
  pending: 'Pending',
  sent: 'Sent',
  delivered: 'Delivered',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

/**
 * How the dashboard's SMS Activity card presents each status: its wording, the
 * sentence shown on hover, and the colour of its swatch and bar segment.
 *
 * Separate from REMINDER_STATUS_LABELS because this card reads 'sent' as
 * "Awaiting confirmation", which is what it means once a message is dispatched
 * and no delivery report has arrived. The filter dropdown wants the plain
 * status name instead.
 *
 * Colours are theme tokens, so a palette change carries here for free.
 */
export const SMS_ACTIVITY_LEGEND: Record<
  ReminderStatusKey,
  { label: string; description: string; color: string }
> = {
  delivered: {
    label: 'Delivered',
    description: 'The network confirmed the message reached the handset.',
    color: 'success.main',
  },
  failed: {
    label: 'Failed',
    description:
      'The message was sent but did not get through: the network reported it undelivered, or it was rejected before it could be sent.',
    color: 'error.main',
  },
  sent: {
    label: 'Awaiting confirmation',
    description:
      'Sent, with no delivery report back yet. Most resolve within minutes, but any with no report after three days stay here for good.',
    color: 'info.main',
  },
  pending: {
    label: 'Pending',
    description:
      'Queued and not sent yet, because its send date has not arrived or it is outside sending hours.',
    color: 'warning.main',
  },
  cancelled: {
    label: 'Cancelled',
    description:
      'Deliberately not sent: the child was already vaccinated, there is no usable number, or the send date had passed by more than three days.',
    color: 'text.disabled',
  },
};

/**
 * Display order for the card: what happened, then what is still in flight,
 * then what was never attempted. Drives the legend and the bar from one list,
 * so a swatch always has a matching segment.
 */
export const SMS_ACTIVITY_ORDER: readonly ReminderStatusKey[] = [
  'delivered',
  'failed',
  'sent',
  'pending',
  'cancelled',
];
